import React, { useState, useEffect, useCallback, useRef } from 'react';
import { downloadZip } from 'client-zip';
import { ref, listAll, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Paper,
  Link,
} from '@mui/material';
import { signInWithCustomToken } from 'firebase/auth';
import { auth, switchToReviewFirebase, switchToAppFirebase } from '../tenantloader/firebase';
import { INSPECTORCLICK_API_BASE_URL } from '../appConfig';
import ReviewPageHeader from './ReviewPageHeader';
import ReviewBrowser from './ReviewBrowser';

const centeredPageSx = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  flexDirection: 'column',
  gap: 2,
  bgcolor: 'background.default',
  fontFamily: 'Roboto, sans-serif',
};

const LoadingScreen = () => (
  <Box sx={centeredPageSx}>
    <CircularProgress size={40} />
    <Typography variant="h6" sx={{ fontWeight: 500 }}>Loading inspection...</Typography>
    <Typography variant="body2" color="text.secondary">Please wait</Typography>
  </Box>
);

/**
 * Centered, well-spaced error/not-found screen (same style as ReviewApp 404).
 */
function CenteredMessageScreen({ title, subtitle, message, showBackButton = false }) {
  return (
    <Box sx={centeredPageSx}>
      {title && (
        <Typography variant="h3" sx={{ fontWeight: 500 }}>{title}</Typography>
      )}
      {subtitle && (
        <Typography variant="h6" color="text.secondary">{subtitle}</Typography>
      )}
      {message && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 400 }}>
          {message}
        </Typography>
      )}
      {showBackButton && (
        <Button component={Link} href="/" variant="outlined" sx={{ mt: 1 }}>
          Back to home
        </Button>
      )}
    </Box>
  );
}

function getErrorMessage(status) {
  if (status === 404) return 'Not found';
  if (status === 403) return 'Access denied';
  if (status === 401) return 'Authentication required';
  if (status === 500) return 'Server error';
  return null;
}

async function listAllRecursive(folderRef) {
  const { items, prefixes } = await listAll(folderRef);
  const subItems = await Promise.all(prefixes.map(listAllRecursive));
  return [...items, ...subItems.flat()];
}

function buildRequestUrl(requestId, { accessKey, tenantId }) {
  const base = `${INSPECTORCLICK_API_BASE_URL}/inspectionrequest/${encodeURIComponent(requestId)}`;
  const params = new URLSearchParams();
  if (accessKey) params.set('accessKey', accessKey);
  if (tenantId) params.set('tenantId', tenantId);
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

export default function ReviewPage({
  requestId,
  accessKeyFromUrl,
  tenantId = null,
  currentUser = null,
  getIdToken = null,
  tenantConfig = null,
  onLogout = null,
  initialSearch = null,
  appId = null,
  sha256 = null,
}) {
  const [state, setState] = useState('idle');
  const [data, setData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorStatus, setErrorStatus] = useState(null);
  const [firebaseReady, setFirebaseReady] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(true);
  const [adminView, setAdminView] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('adminView') === 'true';
  });
  const [currentAppInfo, setCurrentAppInfo] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const downloadingRef = useRef(false);

  const handleAdminViewToggle = useCallback((newValue) => {
    setAdminView(newValue);
    const url = new URL(window.location.href);
    if (newValue) {
      url.searchParams.set('adminView', 'true');
    } else {
      url.searchParams.delete('adminView');
    }
    window.history.replaceState({}, '', url.pathname + url.search);
  }, []);

  const handleDownloadAll = useCallback(async () => {
    if (!currentAppInfo?.blobPath || downloadingRef.current) return;
    downloadingRef.current = true;
    setDownloading(true);
    try {
      const allItems = await listAllRecursive(ref(storage, currentAppInfo.blobPath));

      const settled = await Promise.all(
        allItems.map(async (itemRef) => {
          try {
            const url = await getDownloadURL(itemRef);
            const response = await fetch(url);
            if (!response.ok) {
              console.warn(`Skipping ${itemRef.fullPath}: HTTP ${response.status}`);
              return null;
            }
            const relativePath = itemRef.fullPath.replace(currentAppInfo.blobPath + '/', '');
            return { name: relativePath, input: response };
          } catch (err) {
            console.warn(`Skipping ${itemRef.fullPath}:`, err.message);
            return null;
          }
        })
      );

      const files = settled.filter(Boolean);
      if (files.length === 0) {
        console.warn('ReviewPage: no files available to download');
        return;
      }

      const blob = await downloadZip(files).blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      const appIdentifier = currentAppInfo.appId || currentAppInfo.appName || null;
      const installerName = currentAppInfo.installerName || null;
      const version = currentAppInfo.version || null;
      const hasMeaningfulName = appIdentifier || installerName;
      const zipName = hasMeaningfulName
        ? [appIdentifier, installerName, version]
            .filter(Boolean)
            .join('_')
            .replace(/[^a-zA-Z0-9._-]/g, '_') + '.zip'
        : 'inspection-run.zip';
      a.download = zipName;
      a.click();
      URL.revokeObjectURL(objUrl);
    } catch (err) {
      console.error('ReviewPage: download zip failed', err);
    } finally {
      downloadingRef.current = false;
      setDownloading(false);
    }
  }, [currentAppInfo]);

  const hasAccessKey = Boolean(accessKeyFromUrl && accessKeyFromUrl.trim() !== '');
  const isAuthenticated = Boolean(currentUser && getIdToken);

  useEffect(() => {
    return () => switchToAppFirebase();
  }, []);

  const hasRunIds = data && (data.lastInspectionRunId || data.lastAutowatchRunId);

  const fetchWithBearer = useCallback(async () => {
    switchToAppFirebase();
    const urlWithTenant = buildRequestUrl(requestId, { tenantId: tenantId || undefined });
    setState('loading');
    try {
      const token = await getIdToken();
      const response = await fetch(urlWithTenant, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        setErrorMessage(body.Error || response.statusText || 'Request failed');
        setErrorStatus(response.status);
        setState('error');
        return;
      }
      const json = await response.json();
      setData(json);

      const hasIds = json.lastInspectionRunId || json.lastAutowatchRunId;
      if (hasIds) {
        setFirebaseReady(true);
        setIsReadOnly(false);
      }

      setState('success');
    } catch (err) {
      setErrorMessage(err.message || 'Request failed');
      setErrorStatus(null);
      setState('error');
    }
  }, [requestId, tenantId, getIdToken]);

  const fetchInspection = useCallback(async () => {
    if (!requestId) {
      if (isAuthenticated) {
        setFirebaseReady(true);
        setIsReadOnly(false);
        setState('success');
      } else {
        setState('no_key');
      }
      return;
    }

    if (adminView && isAuthenticated && getIdToken) {
      await fetchWithBearer();
      return;
    }

    if (hasAccessKey) {
      const urlWithKey = buildRequestUrl(requestId, {
        accessKey: accessKeyFromUrl.trim(),
        tenantId: tenantId || undefined,
      });
      setState('loading');
      try {
        const response = await fetch(urlWithKey);
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          setErrorMessage(body.Error || response.statusText || 'Request failed');
          setErrorStatus(response.status);
          setState('error');
          return;
        }
        const json = await response.json();
        setData(json);

        const hasIds = json.lastInspectionRunId || json.lastAutowatchRunId;
        if (hasIds && json.customToken) {
          setState('authenticating');
          try {
            switchToReviewFirebase();
            await signInWithCustomToken(auth, json.customToken);
            setFirebaseReady(true);
            setIsReadOnly(true);
          } catch (authErr) {
            console.error('ReviewPage: signInWithCustomToken failed', authErr);
            setErrorMessage('Failed to authenticate for viewing results');
            setState('error');
            return;
          }
        }

        setState('success');
      } catch (err) {
        setErrorMessage(err.message || 'Request failed');
        setErrorStatus(null);
        setState('error');
      }
      return;
    }

    if (isAuthenticated && getIdToken) {
      await fetchWithBearer();
      return;
    }

    setState('no_key');
  }, [requestId, hasAccessKey, accessKeyFromUrl, tenantId, isAuthenticated, getIdToken, adminView, fetchWithBearer]);

  useEffect(() => {
    if (!requestId && !isAuthenticated) {
      if (hasAccessKey) {
        setState('error');
        setErrorMessage('Invalid or missing link. A request ID is required.');
      } else {
        setState('no_key');
      }
      return;
    }

    if (hasAccessKey || isAuthenticated) {
      fetchInspection();
    } else {
      setState('no_key');
    }
  }, [requestId, hasAccessKey, isAuthenticated, fetchInspection]);

  const showHeader = currentUser && tenantConfig && onLogout;

  let content;
  if (!requestId && !isAuthenticated) {
    content = (
      <CenteredMessageScreen
        title="404"
        subtitle="Not found"
        message="Invalid or missing link. A request ID is required."
        showBackButton={false}
      />
    );
  } else if (state === 'no_key') {
    content = (
      <CenteredMessageScreen
        subtitle="No access key present"
        message="Please use a link that includes an access key, or sign in to view this inspection."
        showBackButton={isAuthenticated}
      />
    );
  } else if (state === 'loading' || state === 'authenticating') {
    content = <LoadingScreen />;
  } else if (state === 'error') {
    const statusTitle = getErrorMessage(errorStatus);
    const is404 = errorStatus === 404;
    content = (
      <CenteredMessageScreen
        title={is404 ? '404' : null}
        subtitle={statusTitle || 'Error'}
        message={errorMessage}
        showBackButton={isAuthenticated}
      />
    );
  } else if (state === 'success') {
    if (isAuthenticated && firebaseReady) {
      content = (
        <ReviewBrowser
          requestData={data}
          lastInspectionRunId={data?.lastInspectionRunId || null}
          lastAutowatchRunId={data?.lastAutowatchRunId || null}
          readOnlyFromUrl={adminView ? isReadOnly : true}
          isAuthenticated={adminView}
          getIdToken={adminView ? getIdToken : null}
          tenantId={tenantId}
          requestId={requestId}
          currentUser={currentUser}
          initialSearch={initialSearch}
          appId={appId}
          sha256={sha256}
          onAppInfoChange={setCurrentAppInfo}
        />
      );
    } else if (hasRunIds && firebaseReady) {
      content = (
        <ReviewBrowser
          requestData={data}
          lastInspectionRunId={data.lastInspectionRunId || null}
          lastAutowatchRunId={data.lastAutowatchRunId || null}
          readOnlyFromUrl={isReadOnly}
          isAuthenticated={false}
          getIdToken={null}
          tenantId={tenantId}
          requestId={requestId}
          currentUser={null}
        />
      );  // no onAppInfoChange here — download is only for authenticated users
    } else if (data) {
      content = (
        <Box sx={{
          ...centeredPageSx,
          p: 3,
        }}>
          <Paper variant="outlined" sx={{ p: 3, maxWidth: 480, width: '100%', boxSizing: 'border-box' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Inspection request</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2"><strong>Request ID:</strong> {data.requestId ?? requestId}</Typography>
              <Typography variant="body2"><strong>Status:</strong> {data.status ?? '—'}</Typography>
              {data.installerName != null && (
                <Typography variant="body2"><strong>Installer name:</strong> {data.installerName}</Typography>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Full results coming soon!
            </Typography>
          </Paper>
          {isAuthenticated && (
            <Button component={Link} href="/" variant="outlined" sx={{ mt: 1 }}>
              Back to home
            </Button>
          )}
        </Box>
      );
    } else {
      content = <LoadingScreen />;
    }
  } else {
    content = <LoadingScreen />;
  }

  if (showHeader) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <ReviewPageHeader
          tenantConfig={tenantConfig}
          currentUser={currentUser}
          onLogout={onLogout}
          adminView={adminView}
          onAdminViewToggle={handleAdminViewToggle}
          onDownloadAll={currentAppInfo?.blobPath ? handleDownloadAll : null}
          downloading={downloading}
        />
        <Box sx={{ flex: 1 }}>{content}</Box>
      </Box>
    );
  }
  return content;
}
